import { Controller } from "@hotwired/stimulus"

// Film de présentation sur l'accueil (29/09/2026).
//
// Deux vidéos dans le même cadre :
//  - l'aperçu : 19 s muettes, en boucle, qui tournent dès que le cadre est à
//    l'écran. La vidéo a l'air d'avoir déjà commencé, c'est ce qui donne
//    envie de cliquer (demande d'Omar).
//  - le film : 2 min 04 avec la voix, chargé seulement au clic, en 720p ou
//    1080p selon la largeur réelle du cadre.
//
// Chaque étape part vers /t/beb (event_type "video", meta.action) par le
// relais "beb:track" du contrôleur beb-track posé sur la page :
//   visible   le cadre a été vu à moitié au moins (une fois)
//   apercu    l'aperçu a réellement démarré (une fois ; sinon lecture auto bloquée)
//   lecture   clic sur le bouton ; seconds = temps passé devant l'aperçu
//   progres   10, 25, 50, 75, 90 et 100 % du film (une fois chacun)
//   pause / reprise / muet / plein_ecran
//   temps     à la sortie de page : secondes réellement regardées + % maximum
export default class extends Controller {
  static targets = ["apercu", "film", "lancer", "barre"]
  static values = { film720: String, film1080: String }

  connect() {
    this.envoyes = new Set()
    this.paliers = new Set()
    this.regarde = 0
    this.dernier = null
    this.pctMax = 0
    this.reduit = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.economie = !!(navigator.connection && navigator.connection.saveData)

    this.onApercuJoue = () => this.uneFois("apercu")
    this.apercuTarget.addEventListener("playing", this.onApercuJoue)

    if ("IntersectionObserver" in window) {
      this.io = new IntersectionObserver((entrees) => entrees.forEach((e) => this.visibilite(e)),
        { threshold: [0, 0.15, 0.25, 0.5] })
      this.io.observe(this.element)
    }

    this.onSortie = () => this.envoyerTemps()
    this.onCache = () => { if (document.visibilityState === "hidden") this.envoyerTemps() }
    window.addEventListener("pagehide", this.onSortie)
    document.addEventListener("visibilitychange", this.onCache)
  }

  disconnect() {
    this.io?.disconnect()
    cancelAnimationFrame(this.raf)
    window.removeEventListener("pagehide", this.onSortie)
    document.removeEventListener("visibilitychange", this.onCache)
    this.apercuTarget.removeEventListener("playing", this.onApercuJoue)
  }

  // ---------------------------------------------------------------- aperçu
  visibilite(e) {
    if (e.intersectionRatio >= 0.5) this.uneFois("visible")
    if (this.element.dataset.etat === "film") return
    // Sous « réduire les animations » ou « économiseur de données », l'aperçu
    // ne tourne pas : l'image fixe reste, le bouton aussi.
    if (this.reduit || this.economie) return
    if (e.intersectionRatio >= 0.15) this.jouerApercu()
    else this.apercuTarget.pause()
  }

  jouerApercu() {
    const v = this.apercuTarget
    if (!v.paused) return
    const essai = v.play()
    if (essai && essai.catch) essai.catch(() => {})   // mode économie iOS : l'affiche reste
    this.suivreBarre()
  }

  suivreBarre() {
    cancelAnimationFrame(this.raf)
    const tour = () => {
      const v = this.apercuTarget
      if (v.duration && this.hasBarreTarget) {
        this.barreTarget.style.transform = `scaleX(${v.currentTime / v.duration})`
      }
      if (!v.paused && this.element.dataset.etat !== "film") this.raf = requestAnimationFrame(tour)
    }
    this.raf = requestAnimationFrame(tour)
  }

  // ---------------------------------------------------------------- film
  lancer() {
    if (this.element.dataset.etat === "film") return
    const film = this.filmTarget
    const largeur = this.element.getBoundingClientRect().width * (window.devicePixelRatio || 1)
    film.src = (largeur >= 1100 && !this.economie) ? this.film1080Value : this.film720Value

    this.envoyer("lecture", { seconds: Math.round(this.apercuTarget.currentTime || 0) })
    this.element.dataset.etat = "film"
    this.apercuTarget.pause()
    cancelAnimationFrame(this.raf)
    this.lancerTarget.setAttribute("tabindex", "-1")
    this.lancerTarget.setAttribute("aria-hidden", "true")

    film.hidden = false
    film.muted = false
    this.brancherFilm(film)
    const essai = film.play()
    if (essai && essai.catch) essai.catch(() => {})
    film.focus({ preventScroll: true })
  }

  brancherFilm(film) {
    film.addEventListener("timeupdate", () => {
      const t = film.currentTime
      if (this.dernier !== null) {
        const pas = t - this.dernier
        if (pas > 0 && pas < 1.5) this.regarde += pas     // un saut n'est pas du temps regardé
      }
      this.dernier = t
      if (!film.duration) return
      const pct = (t / film.duration) * 100
      this.pctMax = Math.max(this.pctMax, pct)
      for (const p of [10, 25, 50, 75, 90]) {
        if (pct >= p && !this.paliers.has(p)) { this.paliers.add(p); this.envoyer("progres", { percent: p }) }
      }
    })
    film.addEventListener("seeking", () => { this.dernier = null })
    film.addEventListener("ended", () => {
      this.pctMax = 100
      if (!this.paliers.has(100)) { this.paliers.add(100); this.envoyer("progres", { percent: 100 }) }
      this.envoyerTemps()
    })
    film.addEventListener("pause", () => {
      if (film.ended || film.seeking) return
      this.enPause = true
      this.envoyer("pause", { percent: Math.round(this.pctCourant()) })
    })
    film.addEventListener("play", () => {
      if (!this.enPause) return
      this.enPause = false
      this.envoyer("reprise", { percent: Math.round(this.pctCourant()) })
    })
    film.addEventListener("volumechange", () => {
      if (film.muted || film.volume === 0) this.uneFois("muet", { percent: Math.round(this.pctCourant()) })
    })
    const pleinEcran = () => {
      if (document.fullscreenElement === film || film.webkitDisplayingFullscreen) this.uneFois("plein_ecran")
    }
    document.addEventListener("fullscreenchange", pleinEcran)
    film.addEventListener("webkitbeginfullscreen", () => this.uneFois("plein_ecran"))
  }

  pctCourant() {
    const f = this.filmTarget
    return f.duration ? (f.currentTime / f.duration) * 100 : 0
  }

  // Envoyé à chaque sortie (onglet caché, page quittée, fin du film) ; le
  // tableau de bord garde le maximum par visiteur.
  envoyerTemps() {
    if (this.element.dataset.etat !== "film" || this.regarde < 1) return
    const cle = `${Math.round(this.regarde)}-${Math.round(this.pctMax)}`
    if (cle === this.dernierTemps) return
    this.dernierTemps = cle
    this.envoyer("temps", { seconds: Math.round(this.regarde), percent: Math.round(this.pctMax) })
  }

  // ---------------------------------------------------------------- envoi
  uneFois(action, meta = {}) {
    if (this.envoyes.has(action)) return
    this.envoyes.add(action)
    this.envoyer(action, meta)
  }

  envoyer(action, meta = {}) {
    window.dispatchEvent(new CustomEvent("beb:track", {
      detail: { event_type: "video", meta: { action: action, ...meta } }
    }))
  }
}
